/**
 * The frame's one per-viewer layout key (1.I-1 deliverable 5): `codex.shell` holds the sidebar's
 * width, whether it is collapsed and the expanded tree nodes. A convenience only: every read
 * and write is wrapped, and a throwing, empty or corrupt store gives the defaults.
 */

/** The kit's sidebar layout (`LAYOUT.sidebar*`, `LAYOUT.rail`), kept here so this module is pure. */
export const SIDEBAR_WIDTH = { default: 280, min: 240, max: 360, rail: 56 } as const;

export const PREFS_KEY = 'codex.shell';
export const MAX_EXPANDED = 200;

export interface ShellPrefs {
  readonly width: number;
  readonly collapsed: boolean;
  readonly expanded: readonly string[];
}

export const DEFAULT_PREFS: ShellPrefs = Object.freeze({
  width: SIDEBAR_WIDTH.default,
  collapsed: false,
  expanded: [],
});

export function clampWidth(value: number): number {
  if (!Number.isFinite(value)) return SIDEBAR_WIDTH.default;
  return Math.min(SIDEBAR_WIDTH.max, Math.max(SIDEBAR_WIDTH.min, Math.round(value)));
}

/** What the store held, or the defaults for anything missing or malformed, field by field. */
export function parsePrefs(raw: string | null | undefined): ShellPrefs {
  if (!raw) return DEFAULT_PREFS;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return DEFAULT_PREFS;
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return DEFAULT_PREFS;
  const record = data as Record<string, unknown>;
  const expanded = Array.isArray(record.expanded)
    ? record.expanded.filter((id): id is string => typeof id === 'string').slice(0, MAX_EXPANDED)
    : [];
  return {
    width: typeof record.width === 'number' ? clampWidth(record.width) : DEFAULT_PREFS.width,
    collapsed: record.collapsed === true,
    expanded,
  };
}

export function serializePrefs(prefs: ShellPrefs): string {
  return JSON.stringify({
    width: clampWidth(prefs.width),
    collapsed: prefs.collapsed,
    expanded: prefs.expanded.slice(0, MAX_EXPANDED),
  });
}

type StoreLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStore(): StoreLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readPrefs(store: StoreLike | null = defaultStore()): ShellPrefs {
  try {
    return parsePrefs(store?.getItem(PREFS_KEY));
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writePrefs(prefs: ShellPrefs, store: StoreLike | null = defaultStore()): void {
  try {
    store?.setItem(PREFS_KEY, serializePrefs(prefs));
  } catch {
    // A convenience only: the frame works without it.
  }
}
